import { useEffect, useState } from 'react';
import { File as FileIcon, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Signed display URLs, shared by every thumbnail on the page. A folder in
 * Extra large icons can show dozens of images, and windows remount as they
 * navigate — without this each mount asked the server again. A signed URL
 * lives about an hour; entries are reused for 45 minutes, and concurrent
 * requests for one document share a single fetch.
 */
const URL_REUSE_MS = 45 * 60 * 1000;
const cache = new Map<string, { url: string; at: number }>();
const inflight = new Map<string, Promise<string>>();

function thumbnailUrl(documentId: string): Promise<string> {
  const hit = cache.get(documentId);
  if (hit && Date.now() - hit.at < URL_REUSE_MS) return Promise.resolve(hit.url);
  const pending = inflight.get(documentId);
  if (pending) return pending;
  const request = api
    .getVaultDocumentDownloadUrl(documentId)
    .then((res) => {
      cache.set(documentId, { url: res.url, at: Date.now() });
      return res.url;
    })
    .finally(() => inflight.delete(documentId));
  inflight.set(documentId, request);
  return request;
}

/** Forget cached URLs (tests; or after a document's bytes were replaced). */
export function clearThumbnailUrlCache(documentId?: string) {
  if (documentId) cache.delete(documentId);
  else cache.clear();
}

/** An image document's picture, lazily loaded from a signed URL. */
export function DocumentThumbnail({
  documentId,
  name,
  iconClassName = 'h-12 w-12',
  fit = 'cover',
}: {
  documentId: string;
  name: string;
  /** Size of the fallback icon, for smaller tiles. */
  iconClassName?: string;
  /** `contain` shows the whole picture (large icons); `cover` fills the tile. */
  fit?: 'cover' | 'contain';
}) {
  const [url, setUrl] = useState<string | null>(() => {
    const hit = cache.get(documentId);
    return hit && Date.now() - hit.at < URL_REUSE_MS ? hit.url : null;
  });
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    thumbnailUrl(documentId)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);
  if (errored) return <FileIcon className={`${iconClassName} text-muted-foreground`} />;
  if (!url) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  return (
    <img
      src={url}
      alt={name}
      loading="lazy"
      draggable={false}
      className={fit === 'contain' ? 'h-full w-full object-contain' : 'h-full w-full object-cover'}
      onError={() => {
        // An expired or revoked URL: drop it so the next mount asks again.
        cache.delete(documentId);
        setErrored(true);
      }}
    />
  );
}
